import { useEffect, useState } from "react";
import { Heart, Coins, Sparkles, Star, Package, Zap, Droplet, Repeat } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Compact reward band shown directly above the action panel so the player
 * always sees the payoff of their last move. Fades in on each new turn (keyed
 * by `turn`), then holds while the player reads the choices.
 *
 * Rendered ONLY when at least one delta is non-zero or the player leveled up.
 *
 * iter67.2 — When a dropped weapon beats the currently-equipped one, we show
 * an inline "SWAP?" button right beside the drop badge so loot feels usable,
 * not just collected.
 */
export default function TurnRewards({
  turn = 0,
  hpDelta = 0,
  goldDelta = 0,
  xpDelta = 0,
  levelUp = false,
  itemsGained = [],
  crit = false,
  lifesteal = 0,
  equippedWeaponAttack = 0,
  equippedWeaponId = null,
  onSwapped = null,
}) {
  const [show, setShow] = useState(false);
  const [swappedIds, setSwappedIds] = useState(new Set());
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (hpDelta || goldDelta || xpDelta || levelUp || (itemsGained && itemsGained.length) || crit || lifesteal) {
      setShow(true);
    } else {
      setShow(false);
    }
    // Reset swap-tracker on fresh turn so a new drop can still show the prompt.
    setSwappedIds(new Set());
  }, [turn, hpDelta, goldDelta, xpDelta, levelUp, itemsGained, crit, lifesteal]);

  const handleSwap = async (itemId) => {
    if (busyId) return;
    setBusyId(itemId);
    try {
      const res = await api.post("/inventory/equip", { item_id: itemId });
      toast.success("Equipped — your next strike will feel different.");
      setSwappedIds((old) => new Set(old).add(itemId));
      onSwapped && onSwapped(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not equip");
    }
    setBusyId(null);
  };

  if (!show) return null;

  return (
    <div
      key={turn}
      data-testid="turn-rewards"
      className="panel px-4 py-3 flex flex-wrap items-center gap-3 fade-up"
      style={{ background: "linear-gradient(90deg, rgba(212,175,55,0.06), rgba(0,0,0,0))" }}
    >
      <span className="text-[10px] tracking-[0.4em] ash-text mr-1">— GAINED —</span>

      {crit && (
        <RewardBadge
          testid="reward-crit"
          icon={<Zap size={12} />}
          label="CRIT · ×1.75"
          tone="crit"
          bold
        />
      )}
      {lifesteal > 0 && (
        <RewardBadge
          testid="reward-lifesteal"
          icon={<Droplet size={12} />}
          label={`+${lifesteal} HP · LIFESTEAL`}
          tone="lifesteal"
        />
      )}
      {hpDelta !== 0 && (
        <RewardBadge
          testid={`reward-hp-${hpDelta > 0 ? "gain" : "loss"}`}
          icon={<Heart size={12} />}
          label={hpDelta > 0 ? `+${hpDelta} HP` : `${hpDelta} HP`}
          tone={hpDelta > 0 ? "heal" : "damage"}
        />
      )}
      {goldDelta !== 0 && (
        <RewardBadge
          testid={`reward-gold-${goldDelta > 0 ? "gain" : "loss"}`}
          icon={<Coins size={12} />}
          label={goldDelta > 0 ? `+${goldDelta} GOLD` : `${goldDelta} GOLD`}
          tone={goldDelta > 0 ? "gold" : "loss"}
        />
      )}
      {xpDelta > 0 && (
        <RewardBadge
          testid="reward-xp"
          icon={<Sparkles size={12} />}
          label={`+${xpDelta} XP`}
          tone="xp"
        />
      )}
      {(itemsGained || []).map((it, i) => {
        // iter67.1 — color-grade server-rolled weapon/armor drops by tier for a loot-rush feel.
        const tone =
          it.tier === "legendary" ? "legendary" :
          it.tier === "epic"      ? "epic"      :
          it.tier === "rare"      ? "rare"      : "item";
        const prefix =
          it.tier === "legendary" ? "LEGENDARY · " :
          it.tier === "epic"      ? "EPIC · "      :
          it.tier === "rare"      ? "RARE · "      : "";
        // iter67.2 — offer "SWAP?" when a dropped weapon out-attacks the equipped one.
        const isBetterWeapon = it.kind === "weapon"
          && it.item_id !== equippedWeaponId
          && (it.attack || 0) > (equippedWeaponAttack || 0);
        const alreadySwapped = swappedIds.has(it.item_id);
        return (
          <span key={`item-${i}-${it.item_id}`} className="inline-flex items-center gap-1">
            <RewardBadge
              testid={`reward-item-${it.item_id}`}
              icon={<Package size={12} />}
              label={`${prefix}+${it.count} ${(it.name || it.item_id).toUpperCase()}`}
              tone={tone}
              bold={tone !== "item"}
            />
            {isBetterWeapon && !alreadySwapped && (
              <button
                type="button"
                onClick={() => handleSwap(it.item_id)}
                disabled={busyId === it.item_id}
                data-testid={`swap-btn-${it.item_id}`}
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-widest gold-text rounded-sm border border-[#d4af37]/70 hover:bg-[#d4af37]/15 transition-colors disabled:opacity-50 disabled:cursor-wait"
                title={`+${(it.attack || 0) - (equippedWeaponAttack || 0)} attack vs. equipped`}
                style={{ boxShadow: "0 0 10px rgba(212,175,55,0.35)" }}
              >
                <Repeat size={10} />
                SWAP? +{(it.attack || 0) - (equippedWeaponAttack || 0)} ATK
              </button>
            )}
            {isBetterWeapon && alreadySwapped && (
              <span className="text-[10px] tracking-widest text-[#7cffa0]" data-testid={`swap-done-${it.item_id}`}>
                EQUIPPED
              </span>
            )}
          </span>
        );
      })}
      {levelUp && (
        <RewardBadge
          testid="reward-level-up"
          icon={<Star size={12} />}
          label="LEVEL UP · +MAX HP"
          tone="levelup"
          bold
        />
      )}
    </div>
  );
}

function RewardBadge({ icon, label, tone, testid, bold }) {
  const toneStyles = {
    heal:      { color: "#7cffa0", border: "rgba(124,255,160,0.4)", shadow: "0 0 12px rgba(124,255,160,0.25)" },
    damage:    { color: "#ff6b3a", border: "rgba(255,107,58,0.4)",  shadow: "0 0 12px rgba(255,107,58,0.25)"  },
    gold:      { color: "#d4af37", border: "rgba(212,175,55,0.5)",  shadow: "0 0 12px rgba(212,175,55,0.3)"   },
    loss:      { color: "#a08a4a", border: "rgba(160,138,74,0.3)",  shadow: "none"                            },
    xp:        { color: "#a78bfa", border: "rgba(167,139,250,0.4)", shadow: "0 0 12px rgba(167,139,250,0.25)" },
    item:      { color: "#f0e6d2", border: "rgba(240,230,210,0.5)", shadow: "0 0 12px rgba(240,230,210,0.2)"  },
    rare:      { color: "#7fc8ff", border: "rgba(127,200,255,0.7)", shadow: "0 0 18px rgba(127,200,255,0.5)"  },
    epic:      { color: "#c48bff", border: "rgba(196,139,255,0.75)",shadow: "0 0 22px rgba(196,139,255,0.6)"  },
    legendary: { color: "#ffcd4a", border: "rgba(255,205,74,0.85)", shadow: "0 0 28px rgba(255,205,74,0.75)"  },
    levelup:   { color: "#f0e6d2", border: "rgba(212,175,55,0.7)",  shadow: "0 0 18px rgba(212,175,55,0.5)"   },
    crit:      { color: "#ffb56b", border: "rgba(255,181,107,0.7)", shadow: "0 0 20px rgba(255,181,107,0.55)" },
    lifesteal: { color: "#ff5f8a", border: "rgba(255,95,138,0.55)", shadow: "0 0 14px rgba(255,95,138,0.35)"  },
  }[tone] || {};
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs tracking-widest ${bold ? "font-heading" : ""}`}
      style={{
        color: toneStyles.color,
        border: `1px solid ${toneStyles.border}`,
        boxShadow: toneStyles.shadow,
        background: "rgba(0,0,0,0.35)",
      }}
    >
      {icon}
      {label}
    </span>
  );
}
